/**
 * LedgerEventsFeed.tsx
 * Component to display a real-time feed of ledger events for a company
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, ArrowRightLeft, RefreshCw, FileText } from 'lucide-react';
import { useLedgerEvents } from '@/hooks/useLedgerEvents';
import { format } from 'date-fns';

interface LedgerEventsFeedProps {
  companyId?: string;
}

const getEventIcon = (eventType: string) => {
  switch (eventType) {
    case 'ISSUANCE':
      return <TrendingUp className="h-4 w-4" />;
    case 'TRANSFER':
      return <ArrowRightLeft className="h-4 w-4" />;
    case 'CANCELLATION':
      return <TrendingDown className="h-4 w-4" />;
    case 'SAFE_CONVERSION':
      return <RefreshCw className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const getEventColor = (eventType: string) => {
  switch (eventType) {
    case 'ISSUANCE':
      return 'default';
    case 'TRANSFER':
      return 'secondary';
    case 'CANCELLATION':
      return 'destructive';
    case 'SAFE_CONVERSION':
      return 'outline';
    default:
      return 'default';
  }
};

export function LedgerEventsFeed({ companyId }: LedgerEventsFeedProps) {
  const { events, loading } = useLedgerEvents(companyId);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border-border bg-card">
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground font-medium">No ledger events yet</p>
        <p className="text-sm text-muted-foreground">Record your first event to start tracking</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {events.map((event) => (
        <Card key={event.id} className="border-border bg-card hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                {getEventIcon(event.event_type)}
                <Badge variant={getEventColor(event.event_type) as any}>{event.event_type}</Badge>
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {format(new Date(event.created_at), 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shares:</span>
              <span className="font-semibold">{event.shares.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Class:</span>
              <span>{event.share_class}</span>
            </div>
            {event.price_per_share && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price/share:</span>
                <span className="font-mono">${event.price_per_share.toFixed(2)}</span>
              </div>
            )}
            {event.note_reference && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Note:</span>
                <span className="truncate max-w-[200px]">{event.note_reference}</span>
              </div>
            )}
            {event.price_per_share && (
              <div className="flex justify-between pt-2 border-t mt-2">
                <span className="text-muted-foreground">Total Value:</span>
                <span className="font-semibold font-mono">
                  $
                  {(event.shares * event.price_per_share).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
